import { z } from "zod";
import { useTRPC } from "../../trpc";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { RelativeLink } from "../../components/Link";
import { useForm } from "@mantine/form";
import { IconExclamationCircle } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { emailSchema, passwordSchemaLength } from "../../utils/schema";
import { captureEvent } from "../../utils/posthog";
import { useSetUser } from "../../hooks/user";
import { useMutation } from "@tanstack/react-query";
import { Event } from "../../constants/events";
import { useNavigateUserAuth } from "./hooks/navigate";
import { zod4Resolver } from "mantine-form-zod-resolver";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";

const defaultFormValues = {
  password: "",
  email: "",
  persistent: false,
};

export function AdminSignIn() {
  const navigate = useNavigateUserAuth();
  const setUser = useSetUser();
  const intl = useIntl();
  const trpc = useTRPC();

  const { mutate, error, isPending } = useMutation(trpc.signIn.mutationOptions({
    onSuccess: (user) => {
      setUser(user);
      navigate(user);
    },
  }));

  const schema = useMemo(() => {
    return z.object({
      email: emailSchema(intl),
      persistent: z.boolean(),
      password: passwordSchemaLength(intl),
    });
  }, [intl]);

  const { onSubmit, getInputProps } = useForm({
    validateInputOnBlur: true,
    initialValues: defaultFormValues,
    validate: zod4Resolver(schema),
  });

  const handleSubmit = useCallback((form: typeof defaultFormValues) => {
    captureEvent(Event.AuthAdminSignedIn, {
      persistent: form.persistent,
    });
    mutate({
      username: form.email,
      password: form.password,
      persistent: form.persistent,
    });
  }, [mutate]);

  let errorMessage: ReactNode = null;
  if (error) {
    if (error.message.includes("User or password are incorrect")) {
      errorMessage = (
        <FormattedMessage
          id="error.invalidEmailOrPassword"
          defaultMessage="Email or password are incorrect"
        />
      );
    }
    else if (error.message.includes("Too many login attempts for user")) {
      errorMessage = (
        <FormattedMessage
          id="error.tooManyLoginAttempts"
          defaultMessage="Too many login attempts"
        />
      );
    }
    else {
      errorMessage = error.message;
    }
  }

  return (
    <Stack>
      <form onSubmit={onSubmit(handleSubmit)}>
        <Stack gap={16}>
          <Title size="h2">
            <FormattedMessage id="title.signIn" defaultMessage="Sign in" />
          </Title>
          {error && (
            <Alert color="red" icon={<IconExclamationCircle />}>
              {errorMessage}
            </Alert>
          )}
          <TextInput
            label={<FormattedMessage id="label.email" defaultMessage="Email" />}
            type="email"
            autoComplete="email"
            required
            {...getInputProps("email")}
          />
          <PasswordInput
            label={<FormattedMessage id="label.password" defaultMessage="Password" />}
            type="password"
            autoComplete="current-password"
            required
            {...getInputProps("password")}
          />
          <Stack gap={16} mt={16}>
            <Group align="center" justify="space-between">
              <Checkbox
                label={<FormattedMessage id="label.rememberMe" defaultMessage="Remember me" />}
                {...getInputProps("persistent")}
              />
              <RelativeLink href="../reset-password">
                <FormattedMessage id="link.forgotPassword" defaultMessage="Forgot password?" />
              </RelativeLink>
            </Group>

            <Button type="submit" fullWidth disabled={isPending} variant="primary">
              <FormattedMessage id="button.signIn" defaultMessage="Sign in" />
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
